import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowUpRightFromSquare,
    faMap,
    faRefresh,
    faSpinner,
    faWarning,
} from "@fortawesome/sharp-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import * as d3 from "d3";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { SiteAnalytics } from "../../../apiClient/generated";
import { useInfrastructureApiClient } from "../../../hooks";
import { ROUTES } from "../../../routes";
import { useResizeObserver } from "../../../ui/Plot/hooks";
import type { AnalyticsFilters } from "../state";
import { BAR_COLOR, BAR_HIGHLIGHT_COLOR } from "../utils/plotColors";
import { renderPlotHeader } from "../utils/plotUtils";
import { getQuarterDateRange } from "../utils/quarterUtils";

const SiteTooltip = ({
    siteData,
    position,
    onEnter,
    onLeave,
    filters,
}: {
    siteData: SiteAnalytics;
    position: { x: number; y: number };
    onEnter: () => void;
    onLeave: () => void;
    filters: AnalyticsFilters;
}) => {
    const infrastructureApiClient = useInfrastructureApiClient();
    const siteQuery = useQuery({
        queryKey: ["site", siteData.id],
        queryFn: async () => {
            return await infrastructureApiClient.infrastructureSitesRetrieve({
                id: siteData.id,
            });
        },
        enabled: !!siteData,
    });

    const handleViewMap = () => {
        // Convert quarter range to date range
        const startRange = getQuarterDateRange(filters.quarterFrom);
        const endRange = getQuarterDateRange(filters.quarterTo);

        const params = new URLSearchParams({
            siteId: siteData.id,
            lat: siteQuery.data?.location.coordinates[1].toString(),
            lon: siteQuery.data?.location.coordinates[0].toString(),
            detectionDateRangeAfter: startRange.start
                .toISOString()
                .split("T")[0],
            detectionDateRangeBefore: endRange.end.toISOString().split("T")[0],
        }).toString();
        const url = `${ROUTES.MAP}?${params}`;
        window.open(url, "_blank");
    };

    const handleViewSitePage = () => {
        // Convert quarter range to date range
        const startRange = getQuarterDateRange(filters.quarterFrom);
        const endRange = getQuarterDateRange(filters.quarterTo);

        const params = new URLSearchParams({
            site: siteData.id,
            detectionDateRangeAfter: startRange.start
                .toISOString()
                .split("T")[0],
            detectionDateRangeBefore: endRange.end.toISOString().split("T")[0],
        }).toString();
        const url = `${ROUTES.INFRASTRUCTURE_SITE}?${params}`;
        window.open(url, "_blank");
    };

    return (
        <div
            role="tooltip"
            className="fixed bg-white text-black px-2 py-1.5 rounded shadow-lg z-[9999] text-sm min-w-44"
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
            }}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
        >
            <div className="font-semibold mb-1">
                {siteData.siteName || siteData.id}
            </div>

            <div className="space-y-0 mb-1.5 text-xs">
                <div className="flex justify-between gap-3">
                    <span>Emissions</span>
                    <span className="font-medium">
                        {siteData.emissionCount}
                    </span>
                </div>
                <div className="flex justify-between gap-3">
                    <span>Emissions below threshold</span>
                    <span className="font-medium">
                        {siteData.emissionsBelowThresholdCount}
                    </span>
                </div>
                <div className="flex justify-between gap-3">
                    <span>Non detects</span>
                    <span className="font-medium">
                        {siteData.nondetectsCount}
                    </span>
                </div>
                <div className="flex justify-between gap-3">
                    <span>% Emissions / site obs.</span>
                    <span className="font-medium">
                        {(siteData.emissionRatio * 100).toFixed(2)}%
                    </span>
                </div>
            </div>

            <div className="flex items-center justify-center gap-2 pt-1 border-t border-gray-200 w-full">
                {siteQuery.isLoading ? (
                    <FontAwesomeIcon
                        icon={faRefresh}
                        className="animate-spin"
                    />
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={handleViewMap}
                            className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                        >
                            <FontAwesomeIcon icon={faMap} />
                            <span>Map</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleViewSitePage}
                            className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                        >
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            <span>Site Page</span>
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

interface PercentagePlotProps {
    svgRef?: React.MutableRefObject<SVGSVGElement | null>;
    siteData: SiteAnalytics[];
    loading?: boolean;
    filters: AnalyticsFilters;
}

export const PercentagePlot = ({
    svgRef: externalSvgRef,
    siteData,
    loading,
    filters,
}: PercentagePlotProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const dimensions = useResizeObserver(containerRef);

    // Hover context handling with external state
    const [hoverContext, setHoverContext] = useState<
        SiteAnalytics | undefined
    >();
    const [tooltipPosition, setTooltipPosition] = useState<{
        x: number;
        y: number;
    }>({ x: 0, y: 0 });
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isHoveringTooltipRef = useRef(false);

    // Sync internal ref to external ref for image export
    useEffect(() => {
        if (externalSvgRef && svgRef.current) {
            externalSvgRef.current = svgRef.current;
        }
    }, [externalSvgRef, dimensions]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, []);

    // Zoom state and refs
    const [currentTransform, setCurrentTransform] = useState<d3.ZoomTransform>(
        d3.zoomIdentity,
    );
    const [zoomDomain, setZoomDomain] = useState<{
        x: [number, number] | null;
        y: [number, number] | null;
    }>({
        x: null,
        y: null,
    });
    const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown>>();

    // Reset handler
    const handleReset = useCallback(() => {
        // Reset both zoom transform and domain
        setZoomDomain({ x: null, y: null });
        setCurrentTransform(d3.zoomIdentity);

        if (!svgRef.current || !zoomBehaviorRef.current) return;
        d3.select(svgRef.current)
            .transition()
            .duration(750)
            .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
    }, []);

    // Tooltip handlers
    const clearHideTimeout = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    }, []);

    const scheduleHideTooltip = useCallback(() => {
        clearHideTimeout();
        hideTimeoutRef.current = setTimeout(() => {
            if (!isHoveringTooltipRef.current) {
                setHoverContext(undefined);
            }
        }, 1000);
    }, [clearHideTimeout]);

    const handleTooltipEnter = useCallback(() => {
        isHoveringTooltipRef.current = true;
        clearHideTimeout();
    }, [clearHideTimeout]);

    const handleTooltipLeave = useCallback(() => {
        isHoveringTooltipRef.current = false;
        scheduleHideTooltip();
    }, [scheduleHideTooltip]);

    const transformedData = useMemo(() => {
        if (!siteData || siteData.length === 0) {
            return [];
        }

        return siteData.toSorted((a, b) => b.emissionRatio - a.emissionRatio);
    }, [siteData]);

    // Setup zoom and brush behaviors (runs when dimensions change)
    useEffect(() => {
        if (dimensions.width === 0 || dimensions.height === 0) return;
        if (!svgRef.current) return;

        const svg = d3.select(svgRef.current);

        // Create zoom behavior (double-click only)
        const zoom = d3
            .zoom<SVGSVGElement, unknown>()
            .scaleExtent([1, 10])
            .on("zoom", (event) => {
                setCurrentTransform(event.transform);
            })
            .filter((event) => {
                // Only allow  double-click, disable drag (drag is for brush)
                return event.type === "dblclick";
            });

        zoomBehaviorRef.current = zoom;

        // Apply zoom to SVG
        svg.call(zoom);

        // Setup double-click to reset
        svg.on("dblclick.zoom", () => {
            setZoomDomain({ x: null, y: null });
            setCurrentTransform(d3.zoomIdentity);
            svg.transition()
                .duration(750)
                .call(zoom.transform, d3.zoomIdentity);
        });
    }, [dimensions]);

    // Reset zoom when data changes
    useEffect(() => {
        setZoomDomain({ x: null, y: null });
        setCurrentTransform(d3.zoomIdentity);
        if (!svgRef.current || !zoomBehaviorRef.current) return;
        d3.select(svgRef.current).call(
            zoomBehaviorRef.current.transform,
            d3.zoomIdentity,
        );
    }, [transformedData]);

    // Effect 3: Render chart (runs on data/transform/dimension changes)
    useEffect(() => {
        if (!transformedData || transformedData.length === 0) return;
        if (dimensions.width === 0 || dimensions.height === 0) return;

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        const margin = { top: 65, right: 10, bottom: 30, left: 75 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Create SVG
        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        // Draw header
        renderPlotHeader({
            svg: svg,
            plotTitle: `Observations Exceeding ${filters.detectionLimit.toFixed(0)} kg/h For The Top 25 Sites`,
            filters,
        });

        // Create the chart group
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Filter data based on box zoom domain
        let visibleData = transformedData;
        if (zoomDomain.x) {
            const allSiteIds = transformedData.map((d) => d.id);
            const startIdx = Math.floor(zoomDomain.x[0]);
            const endIdx = Math.ceil(zoomDomain.x[1]);
            const visibleSiteTypes = allSiteIds.slice(
                Math.max(0, startIdx),
                Math.min(allSiteIds.length, endIdx),
            );
            visibleData = transformedData.filter((d) =>
                visibleSiteTypes.includes(d.id),
            );
        }

        // Create X scale with zoom transform applied to range
        const xScale = d3
            .scaleBand()
            .domain(visibleData.map((d) => d.id))
            .range([0, width].map((d) => currentTransform.applyX(d)))
            .padding(0.2);

        // Create Y scale with box zoom domain and 0% baseline constraint
        const yMax =
            d3.max(visibleData, (v: SiteAnalytics) => v.emissionRatio) || 1;
        let yDomain: [number, number] = [0, yMax];

        if (zoomDomain.y) {
            // Apply box zoom domain, but enforce 0 baseline
            yDomain = [0, zoomDomain.y[1]];
        }

        const yScale = d3
            .scaleLinear()
            .domain(yDomain)
            .range([height, 0].map((d) => currentTransform.applyY(d)));

        // Ensure 0% is always visible by clamping the range
        const yZero = yScale(0);
        if (yZero > height) {
            // Adjust scale to keep 0 visible
            const adjustment = yZero - height;
            yScale.range([height, yScale.range()[1] - adjustment]);
        }

        // Create brush for box zoom
        const brush = d3
            .brush()
            .extent([
                [0, 0],
                [width, height],
            ])
            .on("end", (event) => {
                if (!event.selection) return;

                const [[x0, y0], [x1, y1]] = event.selection as [
                    [number, number],
                    [number, number],
                ];

                // Ignore very small selections
                if (Math.abs(x1 - x0) < 10 || Math.abs(y1 - y0) < 10) {
                    chart.select<SVGGElement>(".brush").call(brush.move, null);
                    return;
                }

                // Convert pixel coordinates to data domain
                // For X: map to site type indices
                // Get current zoom range (or full range if not zoomed)
                const currentStart = zoomDomain.x ? zoomDomain.x[0] : 0;
                const currentEnd = zoomDomain.x
                    ? zoomDomain.x[1]
                    : transformedData.length;
                const currentRange = currentEnd - currentStart;

                // Calculate brush selection as fraction of visible area
                const startFraction = x0 / width;
                const endFraction = x1 / width;

                // Map to absolute indices in transformedData
                const absoluteStart =
                    currentStart + currentRange * startFraction;
                const absoluteEnd = currentStart + currentRange * endFraction;

                const xDomain: [number, number] = [
                    Math.max(0, Math.floor(absoluteStart)),
                    Math.min(transformedData.length, Math.ceil(absoluteEnd)),
                ];

                // For Y: invert scale (SVG y-axis is inverted)
                const yDomain: [number, number] = [
                    Math.max(0, yScale.invert(y1)),
                    yScale.invert(y0),
                ];

                // Apply constraint: always include 0 in Y domain
                yDomain[0] = 0;

                setZoomDomain({ x: xDomain, y: yDomain });

                // Clear brush selection after zoom
                chart.select<SVGGElement>(".brush").call(brush.move, null);
            });

        // Attach brush to chart
        const brushGroup = chart.append("g").attr("class", "brush").call(brush);

        // Style brush selection
        brushGroup
            .select(".selection")
            .attr("fill", "#1677ff")
            .attr("fill-opacity", 0.2)
            .attr("stroke", "#1677ff")
            .attr("stroke-width", 1.5)
            .attr("stroke-dasharray", "4,4");

        // Draw Y-axis gridlines
        chart
            .append("g")
            .attr("class", "y-grid")
            .call(
                d3
                    .axisLeft(yScale)
                    .ticks(5)
                    .tickSize(-width)
                    .tickFormat(() => ""),
            )
            .call((g) => g.select(".domain").remove())
            .call((g) =>
                g
                    .selectAll("line")
                    .attr("stroke", "#ccc")
                    .attr("stroke-opacity", 0.15)
                    .attr("shape-rendering", "crispEdges"),
            );

        // Filter data to only visible items (within viewport)
        const visibleFilteredData = visibleData.filter((d) => {
            const x = xScale(d.id);
            if (x === undefined) return false;
            const barWidth = xScale.bandwidth();
            // Keep bar if any part of it is visible
            return x + barWidth >= 0 && x <= width;
        });

        // Draw bars (only visible ones)
        chart
            .selectAll(".bar")
            .data(visibleFilteredData)
            .join("rect")
            .attr("class", "bar")
            .attr("x", (d: SiteAnalytics) => xScale(d.id) || 0)
            .attr("y", (d: SiteAnalytics) => yScale(d.emissionRatio))
            .attr("width", xScale.bandwidth())
            .attr(
                "height",
                (d: SiteAnalytics) => height - yScale(d.emissionRatio),
            )
            .attr("fill", (d: SiteAnalytics) =>
                d.id === hoverContext?.id ? BAR_HIGHLIGHT_COLOR : BAR_COLOR,
            )
            .on("mouseenter", function (_e, d: SiteAnalytics) {
                // Clear any pending hide timeout
                clearHideTimeout();

                // Get bar position in page coordinates
                const bar = this as SVGRectElement;
                const rect = bar.getBoundingClientRect();

                // Position tooltip at the top
                const tooltipX = rect.right - 20;
                const tooltipY = rect.top - 140;

                setTooltipPosition({ x: tooltipX, y: tooltipY });
                setHoverContext(d);
            })
            .on("mouseleave", () => {
                scheduleHideTooltip();
            });

        // Draw X-axis
        chart
            .append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(xScale).tickFormat(() => ""))
            .selectAll("text")
            .attr("fill", "#666")
            .attr("font-size", "12px");

        // Add X-axis label
        chart
            .append("text")
            .attr("class", "x-axis-label")
            .attr("text-anchor", "middle")
            .attr("x", width / 2)
            .attr("y", height + 25)
            .attr("fill", "#666")
            .attr("font-size", "12px")
            .text("Sites");

        // Draw Y-axis
        chart
            .append("g")
            .attr("class", "y-axis")
            .call(
                d3
                    .axisLeft(yScale)
                    .ticks(5)
                    .tickFormat((d) => `${((d as number) * 100).toFixed(1)}%`),
            )
            .selectAll("text")
            .attr("fill", "#666")
            .attr("font-size", "12px");

        // Add y-axis label
        chart
            .append("text")
            .classed("text-gray-500", true)
            .attr("transform", `rotate(-90) translate(${-height / 2}, -55)`)
            .attr("x", 0)
            .attr("y", 0)
            .attr("text-anchor", "middle")
            .attr("fill", "#666")
            .attr("font-size", "12px")
            .text("Observations Over Threshold (%)");
    }, [
        dimensions,
        transformedData,
        hoverContext,
        currentTransform,
        zoomDomain,
        filters,
        clearHideTimeout,
        scheduleHideTooltip,
    ]);

    return (
        <>
            <div
                ref={containerRef}
                className="relative flex-grow w-full h-full overflow-hidden"
            >
                <svg
                    ref={svgRef}
                    className="w-full h-full z-0"
                    style={{ cursor: "crosshair" }}
                />
                {/* Reset zoom button - only show when zoomed */}
                {(currentTransform.k !== 1 ||
                    currentTransform.x !== 0 ||
                    currentTransform.y !== 0 ||
                    zoomDomain.x !== null ||
                    zoomDomain.y !== null) && (
                    <button
                        type="button"
                        onClick={handleReset}
                        className="absolute top-11 right-2 px-3 py-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 transition-colors text-sm"
                        title="Reset zoom"
                    >
                        Reset Zoom
                    </button>
                )}
                {/* Loading overlay */}
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/90 z-10">
                        <FontAwesomeIcon
                            icon={faSpinner}
                            className="animate-spin"
                        />
                        <div className="text-gray-500">Loading...</div>
                    </div>
                )}
                {/* Empty data overlay */}
                {!loading && transformedData.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/90 z-10">
                        <FontAwesomeIcon icon={faWarning} />
                        <div className="text-gray-500">
                            No data available for selected filters
                        </div>
                    </div>
                )}
            </div>
            {/* Tooltip */}
            {hoverContext &&
                createPortal(
                    <SiteTooltip
                        siteData={hoverContext}
                        position={tooltipPosition}
                        onEnter={handleTooltipEnter}
                        onLeave={handleTooltipLeave}
                        filters={filters}
                    />,
                    document.body,
                )}
        </>
    );
};
